import React from 'react';
import {
  alpha,
  Box,
  Button,
  Container,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FactoryRoundedIcon from '@mui/icons-material/FactoryRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
  motion,
  useReducedMotion,
} from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePublicLeadForm } from '../hooks/usePublicLeadForm';
import type { PublicLeadInterest } from '../services/publicLeadService';

type RevealDirection =
  | 'up'
  | 'left'
  | 'right'
  | 'none';

type RevealSectionProps = {
  children: React.ReactNode;
  delay?: number;
  direction?: RevealDirection;
  distance?: number;
  amount?: number;
};

const RevealSection = ({
  children,
  delay = 0,
  direction = 'up',
  distance = 30,
  amount = 0.16,
}: RevealSectionProps) => {
  const shouldReduceMotion = useReducedMotion();

  const initialX =
    direction === 'left'
      ? -distance
      : direction === 'right'
        ? distance
        : 0;

  const initialY =
    direction === 'up'
      ? distance
      : 0;

  return (
    <motion.div
      initial={
        shouldReduceMotion
          ? false
          : {
              opacity: 0,
              x: initialX,
              y: initialY,
              scale: 0.985,
            }
      }
      whileInView={{
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
      }}
      viewport={{
        once: shouldReduceMotion ? true : false,
        amount,
        margin: '-6% 0px -10% 0px',
      }}
      transition={{
        duration: shouldReduceMotion ? 0 : 0.72,
        delay: shouldReduceMotion ? 0 : delay,
        ease: [0.22, 1, 0.36, 1],
      }}
      style={{
        width: '100%',
        willChange: shouldReduceMotion
          ? 'auto'
          : 'transform, opacity',
      }}
    >
      {children}
    </motion.div>
  );
};

const productCards = [
  {
    key: 'pricing',
    label: 'PROAPricing',
    suffix: 'PRICING',
    eyebrow: 'Pricing y márgenes',
    title: 'Pricing y márgenes para ventas B2B complejas.',
    description:
      'Analiza históricos comerciales para detectar fugas de margen, patrones de precio y oportunidades de mejora.',
    accent: '#38bdf8',
    to: '/',
  },
  {
    key: 'industrial',
    label: 'PROAIndustrial',
    suffix: 'INDUSTRIAL',
    eyebrow: 'Operación industrial',
    title: 'Producción, costes, inventario y operación diaria.',
    description:
      'Conecta fábrica, costes, almacén y tareas para entender mejor lo que ocurre en la operación real de cada planta.',
    accent: '#22c55e',
    to: '/industrial',
  },
];

const industrialAreas = [
  {
    icon: PrecisionManufacturingRoundedIcon,
    title: 'Monitoreo de fábrica',
    text:
      'Los operarios registran la actividad productiva y la empresa obtiene visibilidad sobre órdenes, líneas, estados, tiempos y producción real.',
  },
  {
    icon: InsightsRoundedIcon,
    title: 'Control de costes',
    text:
      'Estructura costes directos e indirectos para entender mejor el coste industrial y su impacto en resultados.',
  },
  {
    icon: Inventory2RoundedIcon,
    title: 'Inventario y almacén',
    text:
      'Conecta materiales, movimientos y espacios de almacén con la realidad operativa de planta.',
  },
  {
    icon: RouteRoundedIcon,
    title: 'Tareas y procesos',
    text:
      'Organiza trabajos, responsables, etapas y seguimiento operativo para reducir dispersión y pérdida de información.',
  },
];

function PreviewStatusDot({ color }: { color: string }) {
  return (
    <Box
      sx={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        bgcolor: color,
        boxShadow: `0 0 12px ${alpha(color, 0.7)}`,
        flexShrink: 0,
      }}
    />
  );
}

function PricingIntelligencePreview() {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.4,
        borderRadius: 4,
        bgcolor: alpha('#0f172a', 0.76),
        border: `1px solid ${alpha('#38bdf8', 0.18)}`,
        backdropFilter: 'blur(18px)',
        boxShadow: `0 28px 80px ${alpha('#020617', 0.36)}`,
      }}
    >
      <Stack spacing={2.2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography
              variant="caption"
              sx={{ color: '#38bdf8', fontWeight: 900, letterSpacing: 1.6 }}
            >
              PRICING INTELLIGENCE
            </Typography>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.05rem' }}>
              Margen detectado
            </Typography>
          </Box>

          <Box
            sx={{
              px: 1.3,
              py: 0.55,
              borderRadius: 999,
              bgcolor: alpha('#22c55e', 0.1),
              border: `1px solid ${alpha('#22c55e', 0.2)}`,
            }}
          >
            <Typography variant="caption" sx={{ color: '#86efac', fontWeight: 900 }}>
              ONLINE
            </Typography>
          </Box>
        </Stack>

        <Box>
          <Typography
            sx={{
              color: '#fcd34d',
              fontWeight: 950,
              fontSize: { xs: '2.2rem', md: '2.7rem' },
              lineHeight: 1,
              letterSpacing: '-0.05em',
            }}
          >
            +184.200€
          </Typography>
          <Typography sx={{ mt: 0.8, color: '#64748b', fontSize: '0.86rem' }}>
            Oportunidad anual estimada sobre histórico comercial.
          </Typography>
        </Box>

        <Stack spacing={1.15}>
          {[
            ['Clientes analizados', '428'],
            ['Referencias activas', '12.840'],
            ['Precisión del modelo', '96,8 %'],
          ].map(([label, value]) => (
            <Stack key={label} direction="row" justifyContent="space-between" spacing={2}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                {value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

function FactoryControlPreview() {
  const lines = [
    {
      label: 'Línea 1',
      status: 'Fabricando ahora',
      detail: 'Producto 1 · fin teórico 17:22',
      color: '#22c55e',
      bg: 'rgba(20,83,45,0.18)',
    },
    {
      label: 'Línea 2',
      status: 'Preparada para producción',
      detail: 'Siguiente orden pendiente de inicio',
      color: '#38bdf8',
      bg: 'rgba(8,47,73,0.16)',
    },
    {
      label: 'Línea 3',
      status: 'Incidencia abierta',
      detail: 'Motivo pendiente de revisión',
      color: '#f59e0b',
      bg: 'rgba(120,53,15,0.16)',
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.4,
        borderRadius: 4,
        bgcolor: alpha('#0f172a', 0.78),
        border: `1px solid ${alpha('#22c55e', 0.18)}`,
        backdropFilter: 'blur(18px)',
        boxShadow: `0 28px 80px ${alpha('#020617', 0.34)}`,
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography
              variant="caption"
              sx={{ color: '#22c55e', fontWeight: 900, letterSpacing: 1.6 }}
            >
              CONTROL DE FÁBRICA
            </Typography>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.05rem' }}>
              Estado actual
            </Typography>
          </Box>

          <FactoryRoundedIcon sx={{ color: '#22c55e' }} />
        </Stack>

        <Stack spacing={1.25}>
          {lines.map((line) => (
            <Box
              key={line.label}
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: line.bg,
                border: `1px solid ${alpha(line.color, 0.24)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.2}>
                <PreviewStatusDot color={line.color} />
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  {line.label}
                </Typography>
              </Stack>
              <Typography sx={{ mt: 0.8, color: line.color, fontWeight: 900, fontSize: '0.86rem' }}>
                {line.status}
              </Typography>
              <Typography sx={{ mt: 0.35, color: '#94a3b8', fontSize: '0.78rem' }}>
                {line.detail}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

function CostBreakdownPreview() {
  const layers = [
    { label: 'Materiales', amount: '128.450€', value: 58, color: '#38bdf8' },
    { label: 'Mano de obra', amount: '44.980€', value: 24, color: '#a78bfa' },
    { label: 'Indirectos', amount: '31.620€', value: 18, color: '#fcd34d' },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.4,
        borderRadius: 4,
        bgcolor: alpha('#0f172a', 0.8),
        border: `1px solid ${alpha('#94a3b8', 0.14)}`,
        backdropFilter: 'blur(18px)',
        boxShadow: `0 28px 80px ${alpha('#020617', 0.32)}`,
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: '#a78bfa', fontWeight: 900, letterSpacing: 1.6 }}
          >
            COSTE INDUSTRIAL
          </Typography>
          <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.05rem' }}>
            Desglose por capas
          </Typography>
        </Box>

        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography sx={{ color: '#64748b', fontSize: '0.78rem', fontWeight: 800 }}>
              COSTE TOTAL
            </Typography>
            <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.7rem' }}>
              205.050€
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ color: '#64748b', fontSize: '0.78rem', fontWeight: 800 }}>
              COSTE/UD
            </Typography>
            <Typography sx={{ color: '#38bdf8', fontWeight: 950, fontSize: '1.7rem' }}>
              4,82€
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1.4}>
          {layers.map((layer) => (
            <Box key={layer.label}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography sx={{ color: '#cbd5e1', fontWeight: 800, fontSize: '0.86rem' }}>
                  {layer.label}
                </Typography>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '0.86rem' }}>
                  {layer.amount}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.65 }}>
                <LinearProgress
                  variant="determinate"
                  value={layer.value}
                  sx={{
                    flex: 1,
                    height: 7,
                    borderRadius: 999,
                    bgcolor: alpha('#94a3b8', 0.16),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      bgcolor: layer.color,
                    },
                  }}
                />
                <Typography sx={{ color: '#94a3b8', fontSize: '0.76rem', minWidth: 34 }}>
                  {layer.value}%
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

function ProaSystemPreview() {
  const [activePreview, setActivePreview] = React.useState<
    'pricing' | 'factory' | 'costs'
  >('factory');

  const getPreviewSx = (
    key: 'pricing' | 'factory' | 'costs',
    baseSx: Record<string, unknown>,
  ) => {
    const active = activePreview === key;

    return {
      ...baseSx,
      zIndex: active ? 5 : key === 'factory' ? 4 : key === 'costs' ? 3 : 2,
      opacity: active ? 1 : 0.74,
      transform: active
        ? 'translateY(-10px) scale(1.045)'
        : 'translateY(0) scale(0.96)',
      transformOrigin: 'center center',
      transition:
        'transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.24s ease, filter 0.24s ease',
      filter: active ? 'none' : 'saturate(0.82)',
      cursor: 'default',
      '&:hover': {
        opacity: 1,
        filter: 'none',
      },
    };
  };

  return (
    <Box>
      <Box
        onMouseLeave={() => setActivePreview('factory')}
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'relative',
          minHeight: 620,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: '8% 5% 4% 4%',
            borderRadius: 8,
            background:
              'radial-gradient(circle at 22% 22%, rgba(56,189,248,0.12), transparent 34%), radial-gradient(circle at 78% 42%, rgba(34,197,94,0.16), transparent 36%), radial-gradient(circle at 42% 86%, rgba(167,139,250,0.10), transparent 30%)',
            filter: 'blur(5px)',
            opacity: 0.9,
            pointerEvents: 'none',
          }}
        />

        <Box
          onMouseEnter={() => setActivePreview('pricing')}
          sx={getPreviewSx('pricing', {
            position: 'absolute',
            left: '1%',
            top: '1%',
            width: '54%',
          })}
        >
          <PricingIntelligencePreview />
        </Box>

        <Box
          onMouseEnter={() => setActivePreview('factory')}
          sx={getPreviewSx('factory', {
            position: 'absolute',
            right: '0%',
            top: '18%',
            width: '56%',
          })}
        >
          <FactoryControlPreview />
        </Box>

        <Box
          onMouseEnter={() => setActivePreview('costs')}
          sx={getPreviewSx('costs', {
            position: 'absolute',
            left: '14%',
            bottom: '0%',
            width: '50%',
          })}
        >
          <CostBreakdownPreview />
        </Box>
      </Box>

      <Stack spacing={2.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
        <FactoryControlPreview />
        <PricingIntelligencePreview />
        <CostBreakdownPreview />
      </Stack>
    </Box>
  );
}

function PricingBranchVisual() {
  const decisionRows = [
    {
      customer: 'Cliente 1',
      product: 'Producto 1',
      margin: '34,8 %',
      price: '42,60 €',
      color: '#22c55e',
    },
    {
      customer: 'Cliente 2',
      product: 'Producto 2',
      margin: '27,1 %',
      price: '18,40 €',
      color: '#f59e0b',
    },
    {
      customer: 'Cliente 3',
      product: 'Producto 3',
      margin: '39,3 %',
      price: '64,90 €',
      color: '#38bdf8',
    },
  ];

  const moduleSx = {
    bgcolor: alpha('#0f172a', 0.78),
    backdropFilter: 'blur(18px)',
    boxShadow: `0 26px 70px ${alpha('#020617', 0.3)}`,
    transition:
      'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), border-color 0.3s ease, opacity 0.3s ease',
    '&:hover': {
      transform: 'translateY(-6px)',
    },
  };

  const ProjectedRevenueMetric = () => (
    <Box
      sx={{
        pl: 2,
        py: 0.8,
        borderLeft: `2px solid ${alpha('#38bdf8', 0.7)}`,
      }}
    >
      <Typography
        sx={{
          color: '#38bdf8',
          fontSize: '0.68rem',
          fontWeight: 950,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        Facturación proyectada
      </Typography>

      <Stack direction="row" alignItems="baseline" spacing={1.2} sx={{ mt: 0.6 }}>
        <Typography
          sx={{
            color: '#f8fafc',
            fontWeight: 950,
            fontSize: { xs: '2rem', md: '2.45rem' },
            lineHeight: 1,
            letterSpacing: '-0.055em',
          }}
        >
          1,42M€
        </Typography>

        <Typography
          sx={{
            color: '#64748b',
            fontSize: '0.78rem',
            fontWeight: 800,
          }}
        >
          1,31M€ base
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 1 }}>
        <TrendingUpRoundedIcon sx={{ color: '#22c55e', fontSize: 17 }} />
        <Typography
          sx={{
            color: '#86efac',
            fontSize: '0.75rem',
            fontWeight: 900,
          }}
        >
          +8,4 % sobre la inercia
        </Typography>
      </Stack>
    </Box>
  );

  const ForecastPanel = () => (
    <Paper
      elevation={0}
      sx={{
        ...moduleSx,
        p: { xs: 2.2, md: 2.6 },
        borderRadius: 4,
        border: `1px solid ${alpha('#38bdf8', 0.2)}`,
        width: '100%',
        minHeight: { xs: 286, md: 304 },
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: alpha('#38bdf8', 0.38),
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            sx={{
              color: '#38bdf8',
              fontSize: '0.68rem',
              fontWeight: 950,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
            }}
          >
            Escenario comercial
          </Typography>

          <Typography
            sx={{
              mt: 0.35,
              color: '#f8fafc',
              fontWeight: 900,
              fontSize: '1rem',
            }}
          >
            Previsión de facturación
          </Typography>

          <Typography
            sx={{
              mt: 0.25,
              color: '#64748b',
              fontSize: '0.72rem',
            }}
          >
            Inercia base frente a estrategia recomendada
          </Typography>
        </Box>

        <Box
          sx={{
            px: 1.15,
            py: 0.5,
            borderRadius: 999,
            bgcolor: alpha('#38bdf8', 0.1),
            border: `1px solid ${alpha('#38bdf8', 0.2)}`,
          }}
        >
          <Typography
            sx={{
              color: '#38bdf8',
              fontWeight: 950,
              fontSize: '0.66rem',
              letterSpacing: 0.6,
            }}
          >
            AI FORECAST
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          height: { xs: 150, md: 164 },
          mt: 2.2,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              `linear-gradient(${alpha('#94a3b8', 0.08)} 1px, transparent 1px)`,
            backgroundSize: '100% 33.333%',
            pointerEvents: 'none',
          }}
        />

        <svg
          viewBox="0 0 560 190"
          preserveAspectRatio="none"
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'visible',
          }}
        >
          <path
            d="M18 146 C80 138, 112 126, 164 130 C220 134, 244 104, 300 111 C350 117, 386 94, 432 100 C474 105, 512 86, 542 91"
            fill="none"
            stroke="rgba(100,116,139,0.72)"
            strokeWidth="3"
            strokeDasharray="7 8"
            strokeLinecap="round"
          />

          <path
            d="M18 151 C78 145, 112 129, 164 120 C214 111, 244 97, 298 88 C352 79, 386 68, 430 58 C474 48, 508 35, 542 24 L542 178 L18 178 Z"
            fill="rgba(56,189,248,0.08)"
          />

          <path
            d="M18 151 C78 145, 112 129, 164 120 C214 111, 244 97, 298 88 C352 79, 386 68, 430 58 C474 48, 508 35, 542 24"
            fill="none"
            stroke="#38bdf8"
            strokeWidth="4"
            strokeLinecap="round"
          />

          <path
            d="M430 58 C474 47, 510 29, 542 18"
            fill="none"
            stroke="#fcd34d"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="6 7"
          />

          <circle cx="430" cy="58" r="5" fill="#38bdf8" />
          <circle cx="542" cy="18" r="6" fill="#fcd34d" />
        </svg>
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ mt: 0.8, px: 0.5 }}
      >
        {['M1', 'M2', 'M3', 'M4', 'M5', 'M6'].map((month) => (
          <Typography
            key={month}
            sx={{
              color: '#64748b',
              fontSize: '0.66rem',
              fontWeight: 800,
            }}
          >
            {month}
          </Typography>
        ))}
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 1.6 }}
      >
        {[
          ['Estrategia recomendada', '#38bdf8'],
          ['Inercia base', '#64748b'],
          ['Potencial', '#fcd34d'],
        ].map(([label, color]) => (
          <Stack key={label} direction="row" alignItems="center" spacing={0.65}>
            <Box
              sx={{
                width: 16,
                height: 3,
                borderRadius: 999,
                bgcolor: color,
              }}
            />
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.66rem',
                fontWeight: 800,
              }}
            >
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );

  const OpportunityPanel = () => (
    <Paper
      elevation={0}
      sx={{
        ...moduleSx,
        p: 2,
        borderRadius: 3.5,
        border: `1px solid ${alpha('#fcd34d', 0.23)}`,
        width: '100%',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: alpha('#fcd34d', 0.42),
        },
      }}
    >
      <Typography
        sx={{
          color: '#fcd34d',
          fontSize: '0.66rem',
          fontWeight: 950,
          letterSpacing: 1.3,
          textTransform: 'uppercase',
        }}
      >
        Oportunidad detectada
      </Typography>

      <Typography
        sx={{
          mt: 0.7,
          color: '#f8fafc',
          fontWeight: 950,
          fontSize: '1.8rem',
          lineHeight: 1,
          letterSpacing: '-0.045em',
        }}
      >
        +124k€
      </Typography>

      <Typography
        sx={{
          mt: 0.7,
          color: '#64748b',
          fontSize: '0.7rem',
          lineHeight: 1.45,
        }}
      >
        Potencial adicional identificado sobre el histórico.
      </Typography>

      <Stack spacing={1.05} sx={{ mt: 1.7 }}>
        {[
          ['Situación actual', 58, '#64748b'],
          ['Escenario recomendado', 78, '#38bdf8'],
          ['Potencial analizado', 92, '#fcd34d'],
        ].map(([label, value, color]) => (
          <Box key={label}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.65rem',
                  fontWeight: 800,
                }}
              >
                {label}
              </Typography>

              <Typography
                sx={{
                  color,
                  fontSize: '0.65rem',
                  fontWeight: 900,
                }}
              >
                {value}%
              </Typography>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={Number(value)}
              sx={{
                mt: 0.45,
                height: 5,
                borderRadius: 999,
                bgcolor: alpha('#94a3b8', 0.12),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor: String(color),
                },
              }}
            />
          </Box>
        ))}
      </Stack>
    </Paper>
  );

  const DemandPanel = () => (
    <Paper
      elevation={0}
      sx={{
        ...moduleSx,
        p: 1.8,
        borderRadius: 3.5,
        border: `1px solid ${alpha('#22c55e', 0.2)}`,
        width: '100%',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: alpha('#22c55e', 0.4),
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            sx={{
              color: '#22c55e',
              fontSize: '0.64rem',
              fontWeight: 950,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
            }}
          >
            Demanda prevista
          </Typography>

          <Typography
            sx={{
              mt: 0.45,
              color: '#f8fafc',
              fontSize: '1.45rem',
              fontWeight: 950,
              letterSpacing: '-0.04em',
            }}
          >
            +8,6 %
          </Typography>
        </Box>

        <TrendingUpRoundedIcon sx={{ color: '#22c55e', fontSize: 21 }} />
      </Stack>

      <Stack
        direction="row"
        alignItems="flex-end"
        spacing={0.8}
        sx={{ height: 52, mt: 1.5 }}
      >
        {[38, 48, 44, 63, 72, 86].map((height, index) => (
          <Box
            key={`${height}-${index}`}
            sx={{
              flex: 1,
              height: `${height}%`,
              minWidth: 8,
              borderRadius: '5px 5px 2px 2px',
              bgcolor:
                index === 5
                  ? alpha('#22c55e', 0.8)
                  : alpha('#38bdf8', 0.42),
            }}
          />
        ))}
      </Stack>
    </Paper>
  );

  const DecisionsPanel = () => (
    <Paper
      elevation={0}
      sx={{
        ...moduleSx,
        p: { xs: 1.8, md: 2 },
        borderRadius: 3.5,
        border: `1px solid ${alpha('#94a3b8', 0.14)}`,
        width: '100%',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: alpha('#38bdf8', 0.26),
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography
            sx={{
              color: '#38bdf8',
              fontSize: '0.66rem',
              fontWeight: 950,
              letterSpacing: 1.3,
              textTransform: 'uppercase',
            }}
          >
            Decisiones recientes
          </Typography>

          <Typography
            sx={{
              mt: 0.25,
              color: '#64748b',
              fontSize: '0.68rem',
            }}
          >
            Simulación de recomendaciones comerciales
          </Typography>
        </Box>

        <PreviewStatusDot color="#22c55e" />
      </Stack>

      <Stack spacing={0.8} sx={{ mt: 1.5 }}>
        {decisionRows.map((row) => (
          <Box
            key={row.customer}
            sx={{
              px: 1.15,
              py: 0.9,
              borderRadius: 2.2,
              bgcolor: alpha('#1e293b', 0.44),
              border: `1px solid ${alpha('#94a3b8', 0.09)}`,
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={5}>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 850,
                    fontSize: '0.76rem',
                  }}
                >
                  {row.product}
                </Typography>

                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.65rem',
                  }}
                >
                  {row.customer}
                </Typography>
              </Grid>

              <Grid item xs={3}>
                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.6rem',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                  }}
                >
                  Margen
                </Typography>

                <Typography
                  sx={{
                    color: row.color,
                    fontWeight: 900,
                    fontSize: '0.75rem',
                  }}
                >
                  {row.margin}
                </Typography>
              </Grid>

              <Grid item xs={4} sx={{ textAlign: 'right' }}>
                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.6rem',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                  }}
                >
                  Precio
                </Typography>

                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                    fontSize: '0.75rem',
                  }}
                >
                  {row.price}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
    </Paper>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 840,
        ml: 'auto',
        mr: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '8%',
          right: '4%',
          width: { xs: 260, md: 470 },
          height: { xs: 260, md: 470 },
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(56,189,248,0.15) 0%, rgba(56,189,248,0.045) 38%, transparent 72%)',
          filter: 'blur(10px)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          left: '2%',
          bottom: '2%',
          width: { xs: 220, md: 340 },
          height: { xs: 220, md: 340 },
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(252,211,77,0.08) 0%, transparent 70%)',
          filter: 'blur(12px)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'relative',
        }}
      >
        <Stack spacing={2.6}>
          {/* Franja superior — métricas principales */}
          <Grid container spacing={2.6} alignItems="stretch">
            <Grid
              item
              md={7}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 360,
                }}
              >
                <ProjectedRevenueMetric />
              </Box>
            </Grid>

            <Grid
              item
              md={5}
              sx={{
                display: 'flex',
                minWidth: 0,
              }}
            >
              <DemandPanel />
            </Grid>
          </Grid>

          {/* Franja central — visual principal */}
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
            }}
          >
            <ForecastPanel />
          </Box>

          {/* Franja inferior — oportunidad y decisiones */}
          <Grid container spacing={2.6} alignItems="stretch">
            <Grid
              item
              md={4}
              sx={{
                display: 'flex',
                minWidth: 0,
              }}
            >
              <OpportunityPanel />
            </Grid>

            <Grid
              item
              md={8}
              sx={{
                display: 'flex',
                minWidth: 0,
              }}
            >
              <DecisionsPanel />
            </Grid>
          </Grid>
        </Stack>
      </Box>

      <Stack
        spacing={2.2}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'relative',
        }}
      >
        <ProjectedRevenueMetric />
        <ForecastPanel />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <OpportunityPanel />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DemandPanel />
          </Grid>
        </Grid>

        <DecisionsPanel />
      </Stack>
    </Box>
  );
}

function IndustrialBranchVisual() {
  const warehouseCells = [
    'rack', 'rack', 'aisle', 'floor', 'floor', 'blocked',
    'rack', 'rack', 'aisle', 'floor', 'empty', 'blocked',
    'empty', 'empty', 'aisle', 'rack', 'rack', 'rack',
    'floor', 'floor', 'aisle', 'rack', 'empty', 'empty',
    'blocked', 'empty', 'aisle', 'rack', 'rack', 'floor',
  ];

  const getWarehouseCellSx = (type: string) => {
    if (type === 'aisle') {
      return {
        bgcolor: alpha('#22c55e', 0.62),
        borderColor: alpha('#86efac', 0.76),
      };
    }

    if (type === 'rack') {
      return {
        bgcolor: alpha('#ef4444', 0.68),
        borderColor: alpha('#fecaca', 0.72),
      };
    }

    if (type === 'floor') {
      return {
        bgcolor: alpha('#f59e0b', 0.66),
        borderColor: alpha('#fde68a', 0.76),
      };
    }

    if (type === 'blocked') {
      return {
        bgcolor: alpha('#64748b', 0.62),
        borderColor: alpha('#94a3b8', 0.58),
      };
    }

    return {
      bgcolor: alpha('#ffffff', 0.025),
      borderColor: alpha('#94a3b8', 0.1),
    };
  };

  const productionLines = [
    {
      label: 'Línea 1',
      status: 'Fabricando',
      detail: 'Producto 1 · fin previsto 17:22',
      value: 82,
      color: '#22c55e',
    },
    {
      label: 'Línea 2',
      status: 'Preparada',
      detail: 'Siguiente orden lista para iniciar',
      value: 56,
      color: '#38bdf8',
    },
    {
      label: 'Línea 3',
      status: 'Incidencia',
      detail: 'Revisión operativa pendiente',
      value: 34,
      color: '#f59e0b',
    },
  ];

  const costLayers = [
    { label: 'Materiales', amount: '2,80 €', value: 58, color: '#38bdf8' },
    { label: 'Mano de obra', amount: '1,16 €', value: 24, color: '#a78bfa' },
    { label: 'Indirectos', amount: '0,86 €', value: 18, color: '#fcd34d' },
  ];

  const workflowStages = [
    { label: 'Recepción', left: 6, width: 28, color: '#22c55e' },
    { label: 'Producción', left: 24, width: 45, color: '#38bdf8' },
    { label: 'Revisión', left: 54, width: 31, color: '#a78bfa' },
    { label: 'Cierre', left: 76, width: 18, color: '#fcd34d' },
  ];

  const moduleSx = {
    width: '100%',
    height: '100%',
    borderRadius: 4,
    bgcolor: alpha('#0f172a', 0.78),
    backdropFilter: 'blur(18px)',
    boxShadow: `0 26px 70px ${alpha('#020617', 0.3)}`,
    transition:
      'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), border-color 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
    },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 760,
        ml: 0,
        mr: 'auto',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: '4%',
          top: '4%',
          width: { xs: 260, md: 460 },
          height: { xs: 260, md: 460 },
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(34,197,94,0.14) 0%, rgba(34,197,94,0.035) 40%, transparent 72%)',
          filter: 'blur(12px)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          right: '2%',
          bottom: '3%',
          width: { xs: 220, md: 360 },
          height: { xs: 220, md: 360 },
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(167,139,250,0.09) 0%, transparent 70%)',
          filter: 'blur(12px)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(12, minmax(0, 1fr))',
          },
          gap: { xs: 2.2, md: 2.5 },
        }}
      >
        <Box
          sx={{
            gridColumn: { xs: '1 / -1', md: 'span 7' },
            display: 'flex',
            minWidth: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...moduleSx,
              p: { xs: 2.2, md: 2.5 },
              minHeight: { md: 306 },
              border: `1px solid ${alpha('#22c55e', 0.22)}`,
              '&:hover': {
                transform: 'translateY(-5px)',
                borderColor: alpha('#22c55e', 0.42),
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography
                  sx={{
                    color: '#22c55e',
                    fontSize: '0.68rem',
                    fontWeight: 950,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                  }}
                >
                  Control de fábrica
                </Typography>

                <Typography
                  sx={{
                    mt: 0.35,
                    color: '#f8fafc',
                    fontWeight: 900,
                    fontSize: '1rem',
                  }}
                >
                  Estado operativo de líneas
                </Typography>

                <Typography
                  sx={{
                    mt: 0.3,
                    color: '#64748b',
                    fontSize: '0.72rem',
                  }}
                >
                  Actividad actual, avance e incidencias de planta
                </Typography>
              </Box>

              <PrecisionManufacturingRoundedIcon sx={{ color: '#22c55e' }} />
            </Stack>

            <Grid container spacing={1.1} sx={{ mt: 1.5 }}>
              {[
                ['Líneas activas', '2', '#22c55e'],
                ['Órdenes abiertas', '3', '#38bdf8'],
                ['Incidencias', '1', '#f59e0b'],
              ].map(([label, value, color]) => (
                <Grid item xs={4} key={label}>
                  <Box
                    sx={{
                      p: 1.05,
                      borderRadius: 2.2,
                      bgcolor: alpha(color, 0.065),
                      border: `1px solid ${alpha(color, 0.13)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.59rem',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.35,
                        color,
                        fontWeight: 950,
                        fontSize: '1.1rem',
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Stack spacing={1.05} sx={{ mt: 1.5 }}>
              {productionLines.map((line) => (
                <Box
                  key={line.label}
                  sx={{
                    px: 1.25,
                    py: 1,
                    borderRadius: 2.4,
                    bgcolor: alpha('#1e293b', 0.42),
                    border: `1px solid ${alpha(line.color, 0.13)}`,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1.5}>
                    <Stack direction="row" spacing={0.8} alignItems="center" minWidth={0}>
                      <PreviewStatusDot color={line.color} />
                      <Box minWidth={0}>
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 850,
                            fontSize: '0.76rem',
                          }}
                        >
                          {line.label}
                        </Typography>
                        <Typography
                          noWrap
                          sx={{
                            color: '#64748b',
                            fontSize: '0.63rem',
                          }}
                        >
                          {line.detail}
                        </Typography>
                      </Box>
                    </Stack>

                    <Typography
                      sx={{
                        color: line.color,
                        fontWeight: 900,
                        fontSize: '0.68rem',
                        flexShrink: 0,
                      }}
                    >
                      {line.status}
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={line.value}
                    sx={{
                      mt: 0.7,
                      height: 5,
                      borderRadius: 999,
                      bgcolor: alpha('#94a3b8', 0.12),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        bgcolor: line.color,
                      },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Box>

        <Box
          sx={{
            gridColumn: { xs: '1 / -1', md: 'span 5' },
            display: 'flex',
            minWidth: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...moduleSx,
              p: { xs: 2.2, md: 2.4 },
              minHeight: { md: 306 },
              border: `1px solid ${alpha('#38bdf8', 0.2)}`,
              '&:hover': {
                transform: 'translateY(-5px)',
                borderColor: alpha('#38bdf8', 0.4),
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography
                  sx={{
                    color: '#38bdf8',
                    fontSize: '0.68rem',
                    fontWeight: 950,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                  }}
                >
                  Costes industriales
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    color: '#f8fafc',
                    fontWeight: 900,
                    fontSize: '1rem',
                  }}
                >
                  Coste por capas
                </Typography>
              </Box>

              <InsightsRoundedIcon sx={{ color: '#38bdf8' }} />
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 2 }}>
              <Box>
                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.64rem',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                  }}
                >
                  Coste por unidad
                </Typography>
                <Typography
                  sx={{
                    mt: 0.3,
                    color: '#f8fafc',
                    fontWeight: 950,
                    fontSize: '2rem',
                    lineHeight: 1,
                    letterSpacing: '-0.05em',
                  }}
                >
                  4,82€
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ color: '#86efac', fontWeight: 900, fontSize: '0.72rem' }}>
                  Coste validado
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.64rem' }}>
                  Producto 1
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={1.25} sx={{ mt: 2.2 }}>
              {costLayers.map((layer) => (
                <Box key={layer.label}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography
                      sx={{
                        color: '#cbd5e1',
                        fontWeight: 800,
                        fontSize: '0.72rem',
                      }}
                    >
                      {layer.label}
                    </Typography>
                    <Stack direction="row" spacing={0.8} alignItems="baseline">
                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.64rem',
                          fontWeight: 900,
                        }}
                      >
                        {layer.value}%
                      </Typography>
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontSize: '0.7rem',
                          fontWeight: 900,
                        }}
                      >
                        {layer.amount}
                      </Typography>
                    </Stack>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={layer.value}
                    sx={{
                      mt: 0.55,
                      height: 7,
                      borderRadius: 999,
                      bgcolor: alpha('#94a3b8', 0.12),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        bgcolor: layer.color,
                      },
                    }}
                  />
                </Box>
              ))}
            </Stack>

            <Grid container spacing={1} sx={{ mt: 2 }}>
              {[
                ['Coste directo', '3,96 €'],
                ['Coste indirecto', '0,86 €'],
              ].map(([label, value]) => (
                <Grid item xs={6} key={label}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2.2,
                      bgcolor: alpha('#1e293b', 0.42),
                      border: `1px solid ${alpha('#94a3b8', 0.09)}`,
                    }}
                  >
                    <Typography sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 900 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ mt: 0.25, color: '#f8fafc', fontSize: '0.78rem', fontWeight: 900 }}>
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        <Box
          sx={{
            gridColumn: { xs: '1 / -1', md: 'span 5' },
            display: 'flex',
            minWidth: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...moduleSx,
              p: { xs: 2.2, md: 2.4 },
              minHeight: { md: 300 },
              border: `1px solid ${alpha('#fcd34d', 0.2)}`,
              '&:hover': {
                transform: 'translateY(-5px)',
                borderColor: alpha('#fcd34d', 0.4),
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography
                  sx={{
                    color: '#fcd34d',
                    fontSize: '0.68rem',
                    fontWeight: 950,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                  }}
                >
                  Inventario y almacén
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    color: '#f8fafc',
                    fontWeight: 900,
                    fontSize: '1rem',
                  }}
                >
                  Plano operativo
                </Typography>
                <Typography sx={{ mt: 0.25, color: '#64748b', fontSize: '0.68rem' }}>
                  Almacén 1 · 6 × 5
                </Typography>
              </Box>

              <Inventory2RoundedIcon sx={{ color: '#fcd34d' }} />
            </Stack>

            <Box
              sx={{
                mt: 1.8,
                display: 'grid',
                gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
                gap: 0.55,
              }}
            >
              {warehouseCells.map((type, index) => (
                <Box
                  key={`${type}-${index}`}
                  sx={{
                    aspectRatio: '1 / 1',
                    borderRadius: 0.8,
                    border: '1px solid',
                    ...getWarehouseCellSx(type),
                  }}
                />
              ))}
            </Box>

            <Grid container spacing={1} sx={{ mt: 1.6 }}>
              {[
                ['Ubicaciones', '24'],
                ['Asignadas', '18'],
                ['Disponibles', '6'],
              ].map(([label, value]) => (
                <Grid item xs={4} key={label}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '0.86rem' }}>
                      {value}
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.58rem', fontWeight: 800 }}>
                      {label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              {[
                ['Rack', '#ef4444'],
                ['Pasillo', '#22c55e'],
                ['Suelo', '#f59e0b'],
                ['Bloqueado', '#64748b'],
              ].map(([label, color]) => (
                <Stack key={label} direction="row" spacing={0.55} alignItems="center">
                  <PreviewStatusDot color={color} />
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.62rem', fontWeight: 800 }}>
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>

        <Box
          sx={{
            gridColumn: { xs: '1 / -1', md: 'span 7' },
            display: 'flex',
            minWidth: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...moduleSx,
              p: { xs: 2.2, md: 2.5 },
              minHeight: { md: 300 },
              border: `1px solid ${alpha('#a78bfa', 0.2)}`,
              '&:hover': {
                transform: 'translateY(-5px)',
                borderColor: alpha('#a78bfa', 0.42),
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography
                  sx={{
                    color: '#a78bfa',
                    fontSize: '0.68rem',
                    fontWeight: 950,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                  }}
                >
                  Tareas y procesos
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    color: '#f8fafc',
                    fontWeight: 900,
                    fontSize: '1rem',
                  }}
                >
                  Planificación del expediente
                </Typography>
                <Typography sx={{ mt: 0.25, color: '#64748b', fontSize: '0.68rem' }}>
                  Plan base, etapa actual y previsión operativa
                </Typography>
              </Box>

              <RouteRoundedIcon sx={{ color: '#a78bfa' }} />
            </Stack>

            <Grid container spacing={1} sx={{ mt: 1.7 }}>
              {[
                ['Etapa actual', 'Producción', '#38bdf8'],
                ['Previsión', '18 días', '#f8fafc'],
                ['Margen', '+3 días', '#22c55e'],
              ].map(([label, value, color]) => (
                <Grid item xs={4} key={label}>
                  <Box
                    sx={{
                      p: 1,
                      height: '100%',
                      borderRadius: 2.2,
                      bgcolor: alpha(color, 0.055),
                      border: `1px solid ${alpha(color, 0.11)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.58rem',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.35,
                        color,
                        fontWeight: 900,
                        fontSize: '0.76rem',
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                mt: 1.7,
                position: 'relative',
                height: 132,
                borderRadius: 2.8,
                bgcolor: alpha('#020617', 0.28),
                border: `1px solid ${alpha('#94a3b8', 0.1)}`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  bottom: 22,
                  left: '55%',
                  width: 1,
                  bgcolor: alpha('#38bdf8', 0.42),
                  boxShadow: `0 0 10px ${alpha('#38bdf8', 0.18)}`,
                }}
              />

              {workflowStages.map((stage, index) => (
                <Box
                  key={stage.label}
                  sx={{
                    position: 'absolute',
                    left: `${stage.left}%`,
                    top: 18 + index * 22,
                    width: `${stage.width}%`,
                    height: 11,
                    borderRadius: 999,
                    bgcolor: alpha(stage.color, 0.82),
                    boxShadow: `0 0 12px ${alpha(stage.color, 0.16)}`,
                  }}
                />
              ))}

              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                  position: 'absolute',
                  left: 16,
                  right: 16,
                  bottom: 8,
                }}
              >
                {['Inicio', 'Hoy', 'Objetivo'].map((label) => (
                  <Typography
                    key={label}
                    sx={{
                      color: '#64748b',
                      fontSize: '0.62rem',
                      fontWeight: 800,
                    }}
                  >
                    {label}
                  </Typography>
                ))}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1.4} flexWrap="wrap" useFlexGap sx={{ mt: 1.4 }}>
              {workflowStages.map((stage) => (
                <Stack key={stage.label} direction="row" spacing={0.55} alignItems="center">
                  <PreviewStatusDot color={stage.color} />
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.62rem', fontWeight: 800 }}>
                    {stage.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

export default function ProaLandingPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const {
    formState: contactFormState,
    isSubmitting: isContactSubmitting,
    submitStatus: contactSubmitStatus,
    handleFormChange: handleContactChange,
    handleSubmit: handleContactSubmit,
  } = usePublicLeadForm({
    initialValues: {
      name: '',
      email: '',
      company: '',
      interest: 'undecided',
      message: '',
    },
    requiredFields: ['name', 'email'],
    buildPayload: (values) => ({
      name: values.name,
      email: values.email,
      company: values.company,
      tier: null,
      message: values.message,
      source: 'proa',
      interest: values.interest as PublicLeadInterest,
    }),
  });

  const contactAccent =
    contactFormState.interest === 'pricing'
      ? '#38bdf8'
      : contactFormState.interest === 'industrial'
        ? '#22c55e'
        : contactFormState.interest === 'both'
          ? '#fcd34d'
          : '#a78bfa';

  const isContactReady =
    Boolean(contactFormState.name.trim()) &&
    Boolean(contactFormState.email.trim());

  const contactInputSx = {
    '& .MuiInputLabel-root': {
      color: '#64748b',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: contactAccent,
    },
    '& .MuiOutlinedInput-root': {
      color: '#f8fafc',
      bgcolor: alpha('#020617', 0.3),
      borderRadius: 2.5,
      transition: 'background-color 0.2s ease, border-color 0.2s ease',
      '& fieldset': {
        borderColor: alpha('#94a3b8', 0.17),
      },
      '&:hover fieldset': {
        borderColor: alpha(contactAccent, 0.42),
      },
      '&.Mui-focused fieldset': {
        borderColor: contactAccent,
      },
    },
    '& .MuiSelect-icon': {
      color: '#94a3b8',
    },
    '& .MuiFormHelperText-root': {
      color: '#64748b',
      mx: 0.3,
    },
  };

  const justifiedBodySx = {
    textAlign: {
      xs: 'left',
      md: 'justify',
    },
    textAlignLast: 'auto',
    hyphens: 'none',
    WebkitHyphens: 'none',
    wordBreak: 'normal',
    overflowWrap: 'normal',
  };

  const handleProductNavigation = (target: string) => {
    if (target.startsWith('#')) {
      document
        .getElementById(target.slice(1))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    navigate(target);
  };

  return (
    <Box
      lang="es"
      sx={{
        width: '100%',
        overflowX: 'hidden',
        bgcolor: '#081120',
        color: '#f8fafc',
      }}
    >
      <Box
        id="top"
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          background:
            'radial-gradient(ellipse at 50% 34%, rgba(56,189,248,0.08) 0%, rgba(56,189,248,0.03) 22%, rgba(15,23,42,0) 54%), linear-gradient(180deg, #0f172a 0%, #0c1629 16%, #081120 48%, #081120 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.1,
            backgroundImage:
              'linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage:
              'linear-gradient(to bottom, rgba(0,0,0,0.9), rgba(0,0,0,0.15), transparent)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 10 } }}>
          <RevealSection>
            <Box
              sx={{
                position: 'relative',
                minHeight: { xs: 580, md: 760 },
                pt: { xs: 5, md: 7 },
                pb: { xs: 3, md: 6 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Typography
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '35%',
                  transform: 'translate(-50%, -50%)',
                  color: alpha('#e0f2fe', 0.045),
                  fontSize: { xs: '11rem', sm: '17rem', md: '28rem', lg: '36rem', xl: '42rem' },
                  fontWeight: 950,
                  letterSpacing: '-0.08em',
                  lineHeight: 0.8,
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
              >
                PROA
              </Typography>

              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '35%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: 300, md: 500 },
                  height: { xs: 180, md: 240 },
                  background:
                    'radial-gradient(ellipse at center, rgba(56,189,248,0.09) 0%, rgba(56,189,248,0.026) 26%, transparent 70%)',
                  filter: 'blur(18px)',
                  pointerEvents: 'none',
                }}
              />

              <Stack
                alignItems="center"
                spacing={0}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  maxWidth: 920,
                  mx: 'auto',
                }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="PROA"
                  sx={{
                    height: { xs: 150, sm: 190, md: 260 },
                    width: 'auto',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 0 18px rgba(56,189,248,0.22))',
                  }}
                />

                <Box
                  sx={{
                    mt: { xs: 18, md: 28 },
                    maxWidth: 760,
                  }}
                >
                  <Typography
                    component="h1"
                    sx={{
                      maxWidth: 720,
                      mx: 'auto',
                      color: '#d7e3f4',
                      fontSize: { xs: '0.98rem', md: '1.16rem' },
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.35,
                    }}
                  >
                    Datos más claros para decisiones industriales más precisas.
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1.5,
                      maxWidth: 620,
                      mx: 'auto',
                      color: '#607089',
                      fontSize: { xs: '0.84rem', md: '0.92rem' },
                      lineHeight: 1.7,
                      fontWeight: 500,
                    }}
                  >
                    Software para empresas industriales B2B con necesidades comerciales y operativas complejas.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </RevealSection>

          <RevealSection delay={0.16}>
            <Box
              id="proa-branches"
              sx={{
                mt: { xs: 2, md: 0 },
                pb: { xs: 4, md: 2 },
              }}
            >
              <Grid container spacing={{ xs: 5, md: 0 }} alignItems="stretch">
                {productCards.map((product, index) => (
                  <Grid item xs={12} md={6} key={product.key}>
                    <Box
                      role="link"
                      tabIndex={0}
                      onClick={() => handleProductNavigation(product.to)}
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') {
                          return;
                        }

                        event.preventDefault();
                        handleProductNavigation(product.to);
                      }}
                      sx={{
                        position: 'relative',
                        minHeight: { xs: 260, md: 330 },
                        px: { xs: 1, md: 7 },
                        py: { xs: 2, md: 3 },
                        cursor: 'pointer',
                        outline: 'none',
                        borderRight:
                          index === 0
                            ? { xs: 'none', md: `1px solid ${alpha('#94a3b8', 0.12)}` }
                            : 'none',
                        transition: 'all 0.24s ease',
                        '&:hover': {
                          transform: { xs: 'none', md: 'translateY(-4px)' },
                        },
                        '&:focus-visible': {
                          borderRadius: 3,
                          boxShadow: `0 0 0 2px ${alpha(product.accent, 0.36)}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xs: 10, md: 0 },
                          right: { xs: 20, md: 72 },
                          width: { xs: 170, md: 260 },
                          height: { xs: 170, md: 260 },
                          borderRadius: '50%',
                          background: `radial-gradient(circle, ${alpha(product.accent, 0.14)} 0%, ${alpha(product.accent, 0.035)} 34%, transparent 70%)`,
                          filter: 'blur(8px)',
                          opacity: 0.7,
                          pointerEvents: 'none',
                        }}
                      />

                      <Stack
                        spacing={3}
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          maxWidth: 520,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="overline"
                            sx={{
                              color: product.accent,
                              fontWeight: 900,
                              letterSpacing: 2.4,
                            }}
                          >
                            {product.eyebrow}
                          </Typography>

                          <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1.5 }}>
                            <Box
                              component="img"
                              src="/logo.png"
                              alt={product.label}
                              sx={{
                                height: { xs: 42, md: 52 },
                                width: 'auto',
                                objectFit: 'contain',
                                filter: `drop-shadow(0 0 10px ${alpha(product.accent, 0.22)})`,
                              }}
                            />

                            <Box>
                              <Typography
                                sx={{
                                  color: '#f8fafc',
                                  fontWeight: 950,
                                  fontSize: { xs: '2.1rem', md: '2.65rem' },
                                  letterSpacing: '-0.08em',
                                  lineHeight: 0.9,
                                }}
                              >
                                PROA
                              </Typography>
                              <Typography
                                sx={{
                                  color: '#94a3b8',
                                  fontWeight: 900,
                                  fontSize: { xs: '0.62rem', md: '0.72rem' },
                                  letterSpacing: { xs: 4, md: 6 },
                                  lineHeight: 1,
                                  mt: 0.8,
                                }}
                              >
                                {product.suffix}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>

                        <Box>
                          <Typography
                            variant="h5"
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 850,
                              lineHeight: 1.25,
                              mb: 1.5,
                              maxWidth: 460,
                            }}
                          >
                            {product.title}
                          </Typography>
                          <Typography
                            sx={{
                              ...justifiedBodySx,
                              color: '#94a3b8',
                              lineHeight: 1.75,
                              fontSize: '0.98rem',
                              maxWidth: 500,
                            }}
                          >
                            {product.description}
                          </Typography>
                        </Box>

                        <Button
                          variant="text"
                          endIcon={<ArrowForwardIcon />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleProductNavigation(product.to);
                          }}
                          sx={{
                            alignSelf: 'flex-start',
                            color: product.accent,
                            fontWeight: 900,
                            textTransform: 'none',
                            px: 0,
                            py: 0.5,
                            '&:hover': {
                              bgcolor: 'transparent',
                              color: product.accent,
                              '& .MuiSvgIcon-root': {
                                transform: 'translateX(3px)',
                              },
                            },
                            '& .MuiSvgIcon-root': {
                              transition: 'transform 0.18s ease',
                            },
                          }}
                        >
                          {product.key === 'pricing'
                            ? 'Conocer PROAPricing'
                            : 'Conocer PROAIndustrial'}
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </RevealSection>
        </Container>
      </Box>

      <Box
        id="proa-about"
        sx={{
          py: { xs: 10, md: 16 },
          bgcolor: '#0f172a',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: { xs: 420, md: 920 },
            height: { xs: 420, md: 620 },
            transform: 'translate(-50%, -50%)',
            background:
              'radial-gradient(ellipse at center, rgba(56,189,248,0.08) 0%, rgba(56,189,248,0.025) 34%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <RevealSection
                direction="left"
                distance={34}
                amount={0.22}
              >
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ color: '#38bdf8', fontWeight: 900, letterSpacing: 2 }}
                    >
                      QUÉ ES PROA
                    </Typography>
                    <Typography
                      variant={isMobile ? 'h4' : 'h3'}
                      sx={{
                        mt: 2,
                        color: '#f8fafc',
                        fontWeight: 950,
                        lineHeight: 1.08,
                        letterSpacing: '-0.055em',
                        maxWidth: 560,
                      }}
                    >
                      Software para entender mejor lo que ya ocurre dentro de la empresa.
                    </Typography>
                  </Box>

                  <Stack spacing={2.2} sx={{ maxWidth: 560 }}>
                    <Typography
                      sx={{
                        ...justifiedBodySx,
                        color: '#cbd5e1',
                        lineHeight: 1.85,
                        fontSize: '1.04rem',
                      }}
                    >
                      Muchas empresas industriales ya tienen gran parte de la información que
                      necesitan para mejorar. El problema es que esa información suele estar
                      repartida entre ERP, Excel, partes manuales, sistemas internos y conocimiento
                      de personas concretas.
                    </Typography>

                    <Typography
                      sx={{
                        ...justifiedBodySx,
                        color: '#94a3b8',
                        lineHeight: 1.85,
                        fontSize: '1.04rem',
                      }}
                    >
                      PROA nace para reducir esa distancia entre los datos disponibles y las
                      decisiones reales. No buscamos sustituir el criterio de quienes conocen la
                      empresa. Buscamos darles una base más clara para decidir mejor.
                    </Typography>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ pt: 1 }}
                  >
                    {[
                      ['Datos comerciales', '#38bdf8'],
                      ['Operación industrial', '#22c55e'],
                      ['Costes y procesos', '#a78bfa'],
                    ].map(([label, color]) => (
                      <Stack key={label} direction="row" alignItems="center" spacing={1}>
                        <PreviewStatusDot color={color} />
                        <Typography sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.9rem' }}>
                          {label}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </RevealSection>
            </Grid>

            <Grid item xs={12} md={7}>
              <RevealSection
                direction="right"
                distance={34}
                delay={0.08}
                amount={0.22}
              >
                <ProaSystemPreview />
              </RevealSection>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        id="proa-logic"
        sx={{
          scrollMarginTop: { xs: 72, md: 88 },
          py: { xs: 10, md: 16 },
          bgcolor: '#081120',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="xl">
          <RevealSection>
            <Box sx={{ textAlign: 'center', mb: { xs: 7, md: 10 } }}>
              <Typography
                variant="overline"
                sx={{ color: '#fcd34d', fontWeight: 900, letterSpacing: 2 }}
              >
                UNA MISMA LÓGICA
              </Typography>
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                sx={{
                  mt: 2,
                  color: '#f8fafc',
                  fontWeight: 950,
                  letterSpacing: '-0.055em',
                }}
              >
                Dos realidades dentro de una misma empresa.
              </Typography>
              <Typography
                sx={{
                  mt: 2,
                  mx: 'auto',
                  maxWidth: 790,
                  color: '#94a3b8',
                  lineHeight: 1.8,
                  fontSize: '1.04rem',
                }}
              >
                PROAPricing trabaja sobre la dimensión comercial. PROAIndustrial trabaja sobre la
                dimensión operativa. La lógica es la misma: ordenar datos reales y convertirlos en
                una base útil para decidir.
              </Typography>
            </Box>
          </RevealSection>

          <Stack spacing={{ xs: 10, md: 16 }}>
            {/* PROAPRICING — texto a la izquierda, visual a la derecha */}
            <RevealSection delay={0.08}>
              <Grid
                container
                spacing={{ xs: 6, md: 8, lg: 10 }}
                alignItems="center"
              >
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 520,
                      textAlign: 'left',
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: '#38bdf8',
                        fontWeight: 900,
                        letterSpacing: 2,
                      }}
                    >
                      PROAPRICING
                    </Typography>

                    <Typography
                      variant={isMobile ? 'h4' : 'h3'}
                      sx={{
                        mt: 2,
                        color: '#f8fafc',
                        fontWeight: 950,
                        letterSpacing: '-0.055em',
                        lineHeight: 1.08,
                      }}
                    >
                      La parte comercial: ventas, clientes, precios y márgenes.
                    </Typography>

                    <Typography
                      sx={{
                        ...justifiedBodySx,
                        mt: 2.4,
                        color: '#94a3b8',
                        lineHeight: 1.85,
                        fontSize: '1rem',
                      }}
                    >
                      Para empresas B2B con muchos productos, muchos clientes y condiciones
                      comerciales complejas. El objetivo es detectar dónde se pierde margen,
                      anticipar facturación y convertir el histórico comercial en decisiones de
                      precio más claras.
                    </Typography>

                    <Button
                      variant="text"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => handleProductNavigation('/')}
                      sx={{
                        mt: 3,
                        color: '#38bdf8',
                        fontWeight: 900,
                        textTransform: 'none',
                        px: 0,
                        '&:hover': {
                          bgcolor: 'transparent',
                        },
                      }}
                    >
                      Ver PROAPricing
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: {
                        xs: 'center',
                        md: 'flex-end',
                      },
                    }}
                  >
                    <PricingBranchVisual />
                  </Box>
                </Grid>
              </Grid>
            </RevealSection>

            {/* PROAINDUSTRIAL — visual a la izquierda, texto a la derecha */}
            <Box
              sx={{
                pt: { xs: 9, md: 12 },
                borderTop: `1px solid ${alpha('#94a3b8', 0.12)}`,
              }}
            >
              <RevealSection delay={0.12}>
                <Grid
                  container
                  spacing={{ xs: 6, md: 8, lg: 10 }}
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                    md={7}
                    sx={{
                      order: {
                        xs: 2,
                        md: 1,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: {
                          xs: 'center',
                          md: 'flex-start',
                        },
                      }}
                    >
                      <IndustrialBranchVisual />
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={5}
                    sx={{
                      order: {
                        xs: 1,
                        md: 2,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 620,
                        ml: {
                          xs: 0,
                          md: 'auto',
                        },
                        textAlign: {
                          xs: 'left',
                          md: 'right',
                        },
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          color: '#22c55e',
                          fontWeight: 900,
                          letterSpacing: 2,
                        }}
                      >
                        PROAINDUSTRIAL
                      </Typography>

                      <Typography
                        variant={isMobile ? 'h4' : 'h3'}
                        sx={{
                          mt: 2,
                          color: '#f8fafc',
                          fontWeight: 950,
                          letterSpacing: '-0.055em',
                          lineHeight: 1.08,
                        }}
                      >
                        La parte operativa: fábrica, costes, inventario y tareas.
                      </Typography>

                      <Typography
                        sx={{
                          ...justifiedBodySx,
                          mt: 2.4,
                          color: '#94a3b8',
                          lineHeight: 1.85,
                          fontSize: '1rem',
                        }}
                      >
                        Para fábricas que necesitan visibilidad sobre lo que ocurre en planta y una
                        forma más clara de conectar producción real, costes industriales, almacén y
                        procesos internos.
                      </Typography>

                      <Grid container spacing={2.2} sx={{ mt: 1.8 }}>
                        {[
                          {
                            title: 'Control de fábrica',
                            copy:
                              'Líneas, órdenes, tiempos e incidencias con seguimiento operativo real.',
                            color: '#22c55e',
                          },
                          {
                            title: 'Costes industriales',
                            copy:
                              'Materiales, mano de obra e indirectos leídos por capas de coste.',
                            color: '#38bdf8',
                          },
                          {
                            title: 'Inventario y almacén',
                            copy:
                              'Ubicaciones, movimientos y materiales conectados con la operativa.',
                            color: '#fcd34d',
                          },
                          {
                            title: 'Tareas y procesos',
                            copy:
                              'Etapas, responsables y previsiones para detectar retrasos antes.',
                            color: '#a78bfa',
                          },
                        ].map((item) => (
                          <Grid item xs={12} sm={6} key={item.title}>
                            <Box
                              sx={{
                                pt: 1.2,
                                borderTop: `1px solid ${alpha(item.color, 0.22)}`,
                                height: '100%',
                              }}
                            >
                              <Typography
                                sx={{
                                  color: item.color,
                                  fontSize: '0.7rem',
                                  fontWeight: 900,
                                  letterSpacing: 1.1,
                                  textTransform: 'uppercase',
                                }}
                              >
                                {item.title}
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.7,
                                  color: '#cbd5e1',
                                  fontSize: '0.84rem',
                                  lineHeight: 1.7,
                                }}
                              >
                                {item.copy}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      <Button
                        variant="text"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => handleProductNavigation('/industrial')}
                        sx={{
                          mt: 3.2,
                          color: '#22c55e',
                          fontWeight: 900,
                          textTransform: 'none',
                          px: 0,
                          '&:hover': {
                            bgcolor: 'transparent',
                          },
                        }}
                      >
                        Explorar PROAIndustrial
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </RevealSection>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box id="proa-industrial" sx={{ py: { xs: 10, md: 14 }, bgcolor: '#0b1120' }}>
        <Container maxWidth="lg">
          <RevealSection>
            <Box sx={{ maxWidth: 760, mb: 7 }}>
              <Typography
                variant="overline"
                sx={{ color: '#22c55e', fontWeight: 900, letterSpacing: 2 }}
              >
                PROAINDUSTRIAL
              </Typography>
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                sx={{ mt: 2, color: '#f8fafc', fontWeight: 900, lineHeight: 1.16 }}
              >
                Más visibilidad sobre la operación diaria de fábrica.
              </Typography>
              <Typography
                sx={{
                  ...justifiedBodySx,
                  mt: 2,
                  color: '#94a3b8',
                  lineHeight: 1.85,
                  fontSize: '1.05rem',
                }}
              >
                PROAIndustrial ayuda a conectar las piezas principales de la operación industrial:
                qué se fabrica, en qué línea, con qué tiempos, qué materiales intervienen, qué
                costes se generan y qué tareas quedan pendientes.
              </Typography>
            </Box>
          </RevealSection>

          <Grid container spacing={3}>
            {industrialAreas.map((area, index) => {
              const AreaIcon = area.icon;

              return (
                <Grid item xs={12} sm={6} md={3} key={area.title}>
                  <RevealSection delay={index * 0.1}>
                    <Paper
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: 4,
                        bgcolor: alpha('#1e293b', 0.4),
                        border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                        transition: 'all 0.28s ease',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          borderColor: alpha('#22c55e', 0.35),
                          bgcolor: alpha('#1e293b', 0.58),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 3,
                          display: 'grid',
                          placeItems: 'center',
                          color: '#22c55e',
                          bgcolor: alpha('#22c55e', 0.1),
                          mb: 2.5,
                        }}
                      >
                        <AreaIcon />
                      </Box>
                      <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 900, mb: 1.5 }}>
                        {area.title}
                      </Typography>
                      <Typography
                        sx={{
                          ...justifiedBodySx,
                          textAlign: {
                            xs: 'left',
                            lg: 'justify',
                          },
                          color: '#94a3b8',
                          lineHeight: 1.7,
                          fontSize: '0.95rem',
                        }}
                      >
                        {area.text}
                      </Typography>
                    </Paper>
                  </RevealSection>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      <Box
        id="proa-workflow"
        sx={{
          py: { xs: 11, md: 17 },
          bgcolor: '#0f172a',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '8%',
            left: '-8%',
            width: { xs: 360, md: 720 },
            height: { xs: 360, md: 720 },
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(56,189,248,0.075) 0%, rgba(56,189,248,0.018) 42%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            right: '-10%',
            bottom: '-24%',
            width: { xs: 360, md: 680 },
            height: { xs: 360, md: 680 },
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(34,197,94,0.07) 0%, rgba(34,197,94,0.016) 42%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid
            container
            spacing={{ xs: 8, md: 10, lg: 14 }}
            alignItems="center"
          >
            <Grid item xs={12} md={6}>
              <RevealSection>
                <Box sx={{ maxWidth: 650 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: '#38bdf8',
                      fontWeight: 900,
                      letterSpacing: 2,
                    }}
                  >
                    CÓMO TRABAJA PROA
                  </Typography>

                  <Typography
                    variant={isMobile ? 'h4' : 'h3'}
                    sx={{
                      mt: 2,
                      color: '#f8fafc',
                      fontWeight: 950,
                      letterSpacing: '-0.055em',
                      lineHeight: 1.08,
                    }}
                  >
                    No se trata de sustituir lo que ya funciona.
                  </Typography>

                  <Stack spacing={2.2} sx={{ mt: 3.2 }}>
                    <Typography
                      sx={{
                        ...justifiedBodySx,
                        color: '#cbd5e1',
                        lineHeight: 1.85,
                        fontSize: '1.02rem',
                      }}
                    >
                      PROA parte de la realidad existente de cada empresa: históricos comerciales,
                      ERP, hojas de cálculo, partes de producción, sistemas internos y conocimiento
                      operativo. No busca reemplazar esas fuentes, sino reducir la distancia entre
                      la información disponible y las decisiones reales.
                    </Typography>

                    <Typography
                      sx={{
                        ...justifiedBodySx,
                        color: '#94a3b8',
                        lineHeight: 1.85,
                        fontSize: '1rem',
                      }}
                    >
                      PROAPricing puede funcionar de forma más autónoma sobre datos comerciales.
                      PROAIndustrial requiere una implantación más cercana y adaptada a la forma
                      real de trabajar de cada fábrica.
                    </Typography>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.4, sm: 3 }}
                    sx={{
                      mt: 4,
                      pt: 2.2,
                      borderTop: `1px solid ${alpha('#94a3b8', 0.12)}`,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PreviewStatusDot color="#38bdf8" />
                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.78rem',
                          fontWeight: 800,
                        }}
                      >
                        Más autónomo en Pricing
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <PreviewStatusDot color="#22c55e" />
                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.78rem',
                          fontWeight: 800,
                        }}
                      >
                        Más adaptado en Industrial
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </RevealSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <RevealSection delay={0.12}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 640,
                    ml: { xs: 0, md: 'auto' },
                  }}
                >
                  <Stack spacing={{ xs: 2, md: 2.3 }}>
                    {[
                      {
                        step: '01',
                        label: 'DATOS EXISTENTES',
                        title: 'La información que la empresa ya genera.',
                        text:
                          'ERP, hojas de cálculo, históricos comerciales, partes de producción y sistemas internos.',
                        color: '#38bdf8',
                        offsetLeft: 0,
                        offsetRight: 8,
                        tags: [
                          { label: 'ERP', color: '#38bdf8' },
                          { label: 'Excel', color: '#38bdf8' },
                          { label: 'Partes', color: '#94a3b8' },
                          { label: 'Sistemas internos', color: '#94a3b8' },
                        ],
                      },
                      {
                        step: '02',
                        label: 'CONTEXTO PROA',
                        title: 'Una lectura conectada de la realidad.',
                        text:
                          'Los datos comerciales y operativos adquieren contexto antes de convertirse en una recomendación.',
                        color: '#fcd34d',
                        offsetLeft: 4,
                        offsetRight: 4,
                        tags: [
                          { label: 'PROAPricing', color: '#38bdf8' },
                          { label: 'PROAIndustrial', color: '#22c55e' },
                          { label: 'Contexto común', color: '#fcd34d' },
                        ],
                      },
                      {
                        step: '03',
                        label: 'DECISIONES REALES',
                        title: 'Una base más clara para actuar.',
                        text:
                          'La información se traduce en prioridades concretas para quienes conocen y gestionan la empresa.',
                        color: '#22c55e',
                        offsetLeft: 8,
                        offsetRight: 0,
                        tags: [
                          { label: 'Corregir', color: '#f59e0b' },
                          { label: 'Priorizar', color: '#38bdf8' },
                          { label: 'Anticipar', color: '#22c55e' },
                        ],
                      },
                    ].map((item) => (
                      <Paper
                        key={item.step}
                        elevation={0}
                        sx={{
                          ml: {
                            xs: 0,
                            md: item.offsetLeft,
                          },
                          mr: {
                            xs: 0,
                            md: item.offsetRight,
                          },
                          p: { xs: 2.2, md: 2.5 },
                          borderRadius: 4,
                          bgcolor: alpha('#0f172a', 0.76),
                          border: `1px solid ${alpha(item.color, 0.2)}`,
                          backdropFilter: 'blur(18px)',
                          boxShadow: `0 24px 68px ${alpha('#020617', 0.28)}`,
                          transition:
                            'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), border-color 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            borderColor: alpha(item.color, 0.4),
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={{ xs: 1.6, md: 2 }}
                          alignItems="flex-start"
                        >
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              flexShrink: 0,
                              display: 'grid',
                              placeItems: 'center',
                              borderRadius: '50%',
                              bgcolor: alpha(item.color, 0.08),
                              border: `1px solid ${alpha(item.color, 0.38)}`,
                              boxShadow: `0 0 20px ${alpha(item.color, 0.1)}`,
                            }}
                          >
                            <Typography
                              sx={{
                                color: item.color,
                                fontSize: '0.64rem',
                                fontWeight: 950,
                              }}
                            >
                              {item.step}
                            </Typography>
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                color: item.color,
                                fontSize: '0.66rem',
                                fontWeight: 950,
                                letterSpacing: 1.45,
                              }}
                            >
                              {item.label}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.55,
                                color: '#f8fafc',
                                fontWeight: 900,
                                fontSize: { xs: '1rem', md: '1.08rem' },
                                lineHeight: 1.35,
                              }}
                            >
                              {item.title}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.7,
                                color: '#94a3b8',
                                lineHeight: 1.7,
                                fontSize: '0.86rem',
                              }}
                            >
                              {item.text}
                            </Typography>

                            <Stack
                              direction="row"
                              spacing={0.8}
                              flexWrap="wrap"
                              useFlexGap
                              sx={{ mt: 1.5 }}
                            >
                              {item.tags.map((tag) => (
                                <Box
                                  key={tag.label}
                                  sx={{
                                    px: 1,
                                    py: 0.45,
                                    borderRadius: 999,
                                    bgcolor: alpha(tag.color, 0.07),
                                    border: `1px solid ${alpha(tag.color, 0.15)}`,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: tag.color,
                                      fontSize: '0.62rem',
                                      fontWeight: 850,
                                    }}
                                  >
                                    {tag.label}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              </RevealSection>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        id="proa-next-step"
        sx={{
          py: { xs: 12, md: 18 },
          bgcolor: '#081120',
          position: 'relative',
          overflow: 'hidden',
          borderTop: `1px solid ${alpha('#94a3b8', 0.08)}`,
        }}
      >
        <Typography
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: '50%',
            top: '48%',
            transform: 'translate(-50%, -50%)',
            color: alpha('#f8fafc', 0.018),
            fontWeight: 950,
            fontSize: {
              xs: '8rem',
              sm: '13rem',
              md: '20rem',
            },
            lineHeight: 0.8,
            letterSpacing: '-0.09em',
            whiteSpace: 'nowrap',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          PROA
        </Typography>

        <Box
          sx={{
            position: 'absolute',
            left: '5%',
            top: '20%',
            width: { xs: 260, md: 520 },
            height: { xs: 260, md: 520 },
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(56,189,248,0.075) 0%, rgba(56,189,248,0.018) 42%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            right: '3%',
            bottom: '-18%',
            width: { xs: 260, md: 500 },
            height: { xs: 260, md: 500 },
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(34,197,94,0.07) 0%, rgba(34,197,94,0.016) 42%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1,
          }}
        >
          <RevealSection>
            <Box
              sx={{
                textAlign: 'center',
                maxWidth: 900,
                mx: 'auto',
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: '#94a3b8',
                  fontWeight: 900,
                  letterSpacing: 2,
                }}
              >
                SIGUIENTE PASO
              </Typography>

              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                sx={{
                  mt: 2,
                  color: '#f8fafc',
                  fontWeight: 950,
                  letterSpacing: '-0.055em',
                  lineHeight: 1.08,
                }}
              >
                El punto de partida depende de dónde esté hoy la complejidad.
              </Typography>

              <Typography
                sx={{
                  mt: 2.5,
                  mx: 'auto',
                  maxWidth: 780,
                  color: '#94a3b8',
                  lineHeight: 1.8,
                  fontSize: '1.04rem',
                }}
              >
                Empieza por la dimensión comercial para entender mejor ventas, precios y márgenes.
                Empieza por la dimensión operativa para conectar fábrica, costes, almacén y
                procesos.
              </Typography>
            </Box>

            <Grid
              container
              spacing={0}
              sx={{
                mt: { xs: 7, md: 9 },
                borderTop: `1px solid ${alpha('#94a3b8', 0.13)}`,
                borderBottom: `1px solid ${alpha('#94a3b8', 0.13)}`,
              }}
            >
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                    py: { xs: 4.5, md: 5.5 },
                    pr: { xs: 0, md: 6 },
                    borderBottom: {
                      xs: `1px solid ${alpha('#94a3b8', 0.13)}`,
                      md: 'none',
                    },
                    borderRight: {
                      xs: 'none',
                      md: `1px solid ${alpha('#94a3b8', 0.13)}`,
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: '#38bdf8',
                          fontSize: '0.7rem',
                          fontWeight: 950,
                          letterSpacing: 1.6,
                        }}
                      >
                        PROAPRICING
                      </Typography>

                      <Typography
                        sx={{
                          mt: 1,
                          color: '#f8fafc',
                          fontWeight: 950,
                          fontSize: { xs: '1.45rem', md: '1.65rem' },
                          letterSpacing: '-0.035em',
                        }}
                      >
                        La dimensión comercial.
                      </Typography>
                    </Box>

                    <TrendingUpRoundedIcon
                      sx={{
                        color: '#38bdf8',
                        fontSize: 28,
                      }}
                    />
                  </Stack>

                  <Typography
                    sx={{
                      mt: 2,
                      color: '#94a3b8',
                      lineHeight: 1.75,
                      fontSize: '0.94rem',
                      maxWidth: 480,
                    }}
                  >
                    Para analizar históricos, anticipar facturación y convertir datos comerciales
                    en decisiones de precio más claras.
                  </Typography>

                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate('/')}
                    sx={{
                      mt: 3.2,
                      bgcolor: '#38bdf8',
                      color: '#020617',
                      fontWeight: 900,
                      textTransform: 'none',
                      px: 2.8,
                      py: 1.2,
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#0ea5e9',
                        boxShadow: `0 12px 30px ${alpha('#38bdf8', 0.14)}`,
                      },
                    }}
                  >
                    Explorar PROAPricing
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                    py: { xs: 4.5, md: 5.5 },
                    pl: { xs: 0, md: 6 },
                    textAlign: {
                      xs: 'left',
                      md: 'right',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}
                    sx={{
                      flexDirection: {
                        xs: 'row',
                        md: 'row-reverse',
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: '#22c55e',
                          fontSize: '0.7rem',
                          fontWeight: 950,
                          letterSpacing: 1.6,
                        }}
                      >
                        PROAINDUSTRIAL
                      </Typography>

                      <Typography
                        sx={{
                          mt: 1,
                          color: '#f8fafc',
                          fontWeight: 950,
                          fontSize: { xs: '1.45rem', md: '1.65rem' },
                          letterSpacing: '-0.035em',
                        }}
                      >
                        La dimensión operativa.
                      </Typography>
                    </Box>

                    <FactoryRoundedIcon
                      sx={{
                        color: '#22c55e',
                        fontSize: 28,
                      }}
                    />
                  </Stack>

                  <Typography
                    sx={{
                      mt: 2,
                      ml: { xs: 0, md: 'auto' },
                      color: '#94a3b8',
                      lineHeight: 1.75,
                      fontSize: '0.94rem',
                      maxWidth: 480,
                    }}
                  >
                    Para conectar producción, costes, inventario y procesos mediante una
                    implantación adaptada a la realidad de cada empresa.
                  </Typography>

                  <Button
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate('/industrial')}
                    sx={{
                      mt: 3.2,
                      borderColor: alpha('#22c55e', 0.5),
                      color: '#86efac',
                      fontWeight: 900,
                      textTransform: 'none',
                      px: 2.8,
                      py: 1.2,
                      '&:hover': {
                        borderColor: '#22c55e',
                        bgcolor: alpha('#22c55e', 0.08),
                      },
                    }}
                  >
                    Descubrir PROAIndustrial
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </RevealSection>
        </Container>
      </Box>

      <Box
        id="proa-contact"
        sx={{
          scrollMarginTop: { xs: 72, md: 88 },
          py: { xs: 11, md: 16 },
          bgcolor: '#0f172a',
          position: 'relative',
          overflow: 'hidden',
          borderTop: `1px solid ${alpha('#94a3b8', 0.08)}`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '-10%',
            top: '-24%',
            width: { xs: 360, md: 620 },
            height: { xs: 360, md: 620 },
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(56,189,248,0.075) 0%, rgba(56,189,248,0.016) 44%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            right: '-10%',
            bottom: '-32%',
            width: { xs: 380, md: 680 },
            height: { xs: 380, md: 680 },
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(34,197,94,0.07) 0%, rgba(34,197,94,0.014) 44%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1,
          }}
        >
          <RevealSection>
            <Grid
              container
              spacing={{ xs: 7, md: 10 }}
              alignItems="center"
            >
              <Grid item xs={12} md={5}>
                <Box sx={{ maxWidth: 520 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: '#a78bfa',
                      fontWeight: 900,
                      letterSpacing: 2.2,
                    }}
                  >
                    HABLEMOS
                  </Typography>

                  <Typography
                    variant={isMobile ? 'h4' : 'h3'}
                    sx={{
                      mt: 2,
                      color: '#f8fafc',
                      fontWeight: 950,
                      letterSpacing: '-0.052em',
                      lineHeight: 1.08,
                    }}
                  >
                    Cuéntanos dónde está hoy la complejidad.
                  </Typography>

                  <Typography
                    sx={{
                      ...justifiedBodySx,
                      mt: 2.5,
                      color: '#94a3b8',
                      fontSize: '1.02rem',
                      lineHeight: 1.8,
                    }}
                  >
                    Cada empresa parte de una situación distinta. Explícanos qué
                    información tienes, qué decisiones necesitas mejorar o qué
                    parte de la operación resulta difícil conectar.
                  </Typography>

                  <Typography
                    sx={{
                      ...justifiedBodySx,
                      mt: 2,
                      color: '#64748b',
                      fontSize: '0.9rem',
                      lineHeight: 1.75,
                    }}
                  >
                    No hace falta llegar con una solución definida. La primera
                    conversación sirve para entender el caso y orientar
                    correctamente el siguiente paso.
                  </Typography>

                  <Stack
                    sx={{ mt: 4.5 }}
                    spacing={0}
                  >
                    {[
                      {
                        number: '01',
                        title: 'Entendemos el punto de partida',
                        text: 'Datos, sistemas, decisiones y procesos actuales.',
                      },
                      {
                        number: '02',
                        title: 'Identificamos el área prioritaria',
                        text: 'Pricing, operación industrial o ambas dimensiones.',
                      },
                      {
                        number: '03',
                        title: 'Definimos un siguiente paso realista',
                        text: 'Sin forzar una solución antes de comprender el contexto.',
                      },
                    ].map((item) => (
                      <Box
                        key={item.number}
                        sx={{
                          py: 2,
                          borderTop: `1px solid ${alpha('#94a3b8', 0.12)}`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="flex-start"
                        >
                          <Typography
                            sx={{
                              minWidth: 28,
                              color: '#64748b',
                              fontSize: '0.68rem',
                              fontWeight: 950,
                              letterSpacing: 1.2,
                            }}
                          >
                            {item.number}
                          </Typography>

                          <Box>
                            <Typography
                              sx={{
                                color: '#e2e8f0',
                                fontSize: '0.88rem',
                                fontWeight: 900,
                              }}
                            >
                              {item.title}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.45,
                                color: '#64748b',
                                fontSize: '0.78rem',
                                lineHeight: 1.6,
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Paper
                  component="form"
                  elevation={0}
                  onSubmit={(event) => {
                    event.preventDefault();
                    void handleContactSubmit();
                  }}
                  sx={{
                    p: { xs: 2.5, sm: 3.5, md: 4 },
                    borderRadius: 4,
                    bgcolor: alpha('#081120', 0.76),
                    border: `1px solid ${alpha(contactAccent, 0.24)}`,
                    boxShadow: `0 28px 80px ${alpha('#020617', 0.34)}`,
                    backdropFilter: 'blur(18px)',
                    transition:
                      'border-color 0.25s ease, box-shadow 0.25s ease',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 3.2 }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: contactAccent,
                          fontSize: '0.68rem',
                          fontWeight: 950,
                          letterSpacing: 1.5,
                        }}
                      >
                        CONTACTO PROA
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.5,
                          color: '#f8fafc',
                          fontWeight: 900,
                          fontSize: '1.25rem',
                        }}
                      >
                        Empecemos por entender tu caso.
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: contactAccent,
                          boxShadow: `0 0 12px ${alpha(contactAccent, 0.6)}`,
                        }}
                      />

                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.7rem',
                          fontWeight: 800,
                        }}
                      >
                        Consulta general
                      </Typography>
                    </Stack>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        name="name"
                        label="Nombre completo"
                        value={contactFormState.name}
                        onChange={handleContactChange}
                        autoComplete="name"
                        sx={contactInputSx}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        type="email"
                        name="email"
                        label="Email corporativo"
                        value={contactFormState.email}
                        onChange={handleContactChange}
                        autoComplete="email"
                        sx={contactInputSx}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="company"
                        label="Empresa"
                        value={contactFormState.company}
                        onChange={handleContactChange}
                        autoComplete="organization"
                        sx={contactInputSx}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        name="interest"
                        label="Interés principal"
                        value={contactFormState.interest}
                        onChange={handleContactChange}
                        sx={contactInputSx}
                      >
                        <MenuItem value="pricing">
                          PROAPricing
                        </MenuItem>
                        <MenuItem value="industrial">
                          PROAIndustrial
                        </MenuItem>
                        <MenuItem value="both">
                          Ambas soluciones
                        </MenuItem>
                        <MenuItem value="undecided">
                          Todavía no lo sé
                        </MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={5}
                        name="message"
                        label="¿Qué necesitas entender o mejorar?"
                        value={contactFormState.message}
                        onChange={handleContactChange}
                        sx={contactInputSx}
                      />
                    </Grid>
                  </Grid>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={2}
                    sx={{ mt: 3 }}
                  >
                    <Box>
                      {contactSubmitStatus === 'success' ? (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <CheckCircleRoundedIcon
                            sx={{
                              color: '#22c55e',
                              fontSize: 19,
                            }}
                          />

                          <Typography
                            sx={{
                              color: '#86efac',
                              fontSize: '0.78rem',
                              fontWeight: 800,
                            }}
                          >
                            Consulta recibida correctamente.
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography
                          sx={{
                            color:
                              contactSubmitStatus === 'error'
                                ? '#fca5a5'
                                : '#64748b',
                            fontSize: '0.74rem',
                            lineHeight: 1.55,
                          }}
                        >
                          {contactSubmitStatus === 'error'
                            ? 'No se ha podido enviar. Revisa los datos e inténtalo de nuevo.'
                            : 'Nombre y correo son obligatorios. Usaremos los datos únicamente para responder a esta consulta.'}
                        </Typography>
                      )}
                    </Box>

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={
                        !isContactReady ||
                        isContactSubmitting ||
                        contactSubmitStatus === 'success'
                      }
                      endIcon={
                        contactSubmitStatus === 'success'
                          ? <CheckCircleRoundedIcon />
                          : <SendRoundedIcon />
                      }
                      sx={{
                        minWidth: { xs: '100%', sm: 176 },
                        bgcolor:
                          contactSubmitStatus === 'success'
                            ? '#22c55e'
                            : contactAccent,
                        color: '#020617',
                        fontWeight: 950,
                        textTransform: 'none',
                        px: 3,
                        py: 1.25,
                        boxShadow: `0 12px 28px ${alpha(contactAccent, 0.14)}`,
                        '&:hover': {
                          bgcolor:
                            contactSubmitStatus === 'success'
                              ? '#16a34a'
                              : contactAccent,
                          boxShadow: `0 16px 34px ${alpha(contactAccent, 0.22)}`,
                        },
                        '&.Mui-disabled': {
                          bgcolor:
                            contactSubmitStatus === 'success'
                              ? alpha('#22c55e', 0.8)
                              : alpha(contactAccent, 0.32),
                          color: alpha('#020617', 0.65),
                        },
                      }}
                    >
                      {isContactSubmitting
                        ? 'Enviando...'
                        : contactSubmitStatus === 'success'
                          ? 'Consulta recibida'
                          : 'Enviar consulta'}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </RevealSection>
        </Container>
      </Box>
    </Box>
  );
}